import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitizeWebAnswer } from '../utils/responseSanitizer';

function SourceSection({
  source,
  messages,
  isLoading,
  selectedIds,
  onToggleSelect,
  onRetry,
  onCopy,
  onChipClick,
  isProcessing,
  isExpanded,
  onExpand,
}) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages.length, isLoading]);

  const prevMsgCountRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current && scrollRef.current) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.sender === 'user') {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
          }
        });
      }
    }
    prevMsgCountRef.current = messages.length;
  }, [messages]);

  const lastBotMsg = messages.filter((m) => m.sender === 'bot').slice(-1)[0];
  const busy = isLoading || messages.some((m) => m.retrying);

  const renderMarkdown = (text) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
      {source.id === 'web'
        ? sanitizeWebAnswer(text).replace(/<br\s*\/?>/gi, '\n')
        : text.replace(/<br\s*\/?>/gi, '\n')}
    </ReactMarkdown>
  );

  return (
    <div className={`src-section ${isExpanded ? 'src-section--expanded' : ''}`} style={{ '--src-accent': source.accent }}>
      <div className="src-header" onClick={onExpand} style={{ cursor: 'pointer' }}>
        <div className="src-header-left">
          <div className="src-icon">{source.icon}</div>
          <div className="src-header-text">
            <h3 className="src-title">{source.title}</h3>
            <div className="src-status">
              <span className={`src-status-dot ${busy ? 'loading' : ''}`} />
              <span className="src-status-text">
                {busy ? 'Processing...' : 'Ready'}
              </span>
            </div>
          </div>
        </div>
        <div className="src-header-right">
          {!isExpanded && lastBotMsg && !lastBotMsg.isError && (
            <span className="src-preview-text">
              {lastBotMsg.text.substring(0, 60)}...
            </span>
          )}
          <button className="src-expand-btn" title={isExpanded ? 'Collapse' : 'Expand'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isExpanded ? (
                <><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></>
              ) : (
                <><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>
              )}
            </svg>
          </button>
        </div>
      </div>

      <div className="src-messages" ref={scrollRef}>
        {messages.length === 0 && !isLoading ? (
          <div className="src-empty">
            <div className="src-empty-icon">{source.icon}</div>
            <p className="src-empty-text">{source.emptyMsg}</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isSelected = selectedIds.has(msg.id);
            return (
              <div key={msg.id} className={`src-msg-row ${msg.sender}`}>
                <div className={`src-msg ${msg.sender} ${msg.isError && !msg.retrying ? 'error' : ''} ${isSelected ? 'selected' : ''}`}>
                  {msg.sender === 'user' ? (
                    <div className="src-msg-user">{msg.text}</div>
                  ) : (
                    <div className="src-msg-bot">
                      {msg.retrying ? (
                        <div className="src-retry-stack">
                          <div className="src-retry-ghost" aria-hidden="true">
                            <div className="src-markdown">{renderMarkdown(msg.text)}</div>
                            <div className="src-error-actions">
                              <span className="src-btn-retry">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                                </svg>
                                Retry
                              </span>
                            </div>
                          </div>
                          <div className="src-loading-text src-retry-loading">
                            <div className="uws-typing"><span/><span/><span/></div>
                            <span>Retrying…</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          {msg.text && (
                            <div className="src-markdown">{renderMarkdown(msg.text)}</div>
                          )}

                          {msg.isError && (
                            <div className="src-error-actions">
                              <button className="src-btn-retry" onClick={() => onRetry(source.id, msg.id)}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                                </svg>
                                Retry
                              </button>
                            </div>
                          )}

                          {!msg.isError && msg.text && (
                        <div className="src-msg-actions">
                          <button className="src-action-btn" onClick={() => onCopy(msg.text)} title="Copy response">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                            </svg>
                          </button>
                          <button
                            className={`src-action-btn select-btn ${isSelected ? 'selected' : ''}`}
                            onClick={() => onToggleSelect(msg.id)}
                            title={isSelected ? 'Deselect for summary' : 'Select for summary'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              {isSelected ? (
                                <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
                              ) : (
                                <><circle cx="12" cy="12" r="10"/></>
                              )}
                            </svg>
                            <span>{isSelected ? 'Selected' : 'Select'}</span>
                          </button>
                        </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {isLoading && (
          <div className="src-msg-row bot">
            <div className="src-msg bot loading">
              <div className="src-loading-text">
                <div className="uws-typing"><span/><span/><span/></div>
                <span>{source.loadingMsg}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SourceSection;
